import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const regionToIamHost: Record<string, string> = {
  us: "iam.checkmarx.net",
  us2: "us.iam.checkmarx.net",
  eu: "eu.iam.checkmarx.net",
  eu2: "eu-2.iam.checkmarx.net",
  deu: "deu.iam.checkmarx.net",
  ind: "ind.iam.checkmarx.net",
  anz: "anz.iam.checkmarx.net",
  sng: "sng.iam.checkmarx.net",
  mea: "mea.iam.checkmarx.net",
};

const regionToAstHost: Record<string, string> = {
  us: "ast.checkmarx.net",
  us2: "us.ast.checkmarx.net",
  eu: "eu.ast.checkmarx.net",
  eu2: "eu-2.ast.checkmarx.net",
  deu: "deu.ast.checkmarx.net",
  ind: "ind.ast.checkmarx.net",
  anz: "anz.ast.checkmarx.net",
  sng: "sng.ast.checkmarx.net",
  mea: "mea.ast.checkmarx.net",
};

export const checkmarx = defineConnector({
  identifier: "checkmarx",
  name: "Checkmarx",
  tagLine: "Application security testing",
  logo,
  protocol: "http",
  tags: ["security", "engineering"],
  defaultUrlPattern: () => "https://[^/]*\\.checkmarx\\.net/.*",
  auth: (app) => {
    const region = app.configuration.region || "us";
    const iamHost = regionToIamHost[region] ?? regionToIamHost.us;
    return fetchTokenAuth({
      request: {
        url: `https://${iamHost}/auth/realms/${app.config.tenant}/protocol/openid-connect/token`,
        formBody: {
          grant_type: "refresh_token",
          client_id: "ast-app",
          refresh_token: app.secret.apiKey,
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: { kind: "header", scheme: "Bearer" },
    });
  },
  steps: [
    {
      title: "Get your API key",
      items: [
        {
          kind: "markdown",
          content:
            "Log into your [Checkmarx One](https://checkmarx.com/) account. Navigate to **Settings › Identity & Access Management › API Keys** and create a new API key.",
        },
      ],
    },
    {
      title: "Enter your tenant details",
      items: [
        {
          kind: "input",
          label: "Tenant name",
          placeholder: "your-tenant",
          required: true,
          target: { kind: "configuration", field: "tenant" },
          helperText: "Your Checkmarx tenant name.",
        },
        {
          kind: "select",
          label: "Region",
          defaultValue: "us",
          required: true,
          target: { kind: "configuration", field: "region" },
          options: [
            { value: "us", label: "US (ast.checkmarx.net)" },
            { value: "us2", label: "US 2 (us.ast.checkmarx.net)" },
            { value: "eu", label: "EU (eu.ast.checkmarx.net)" },
            { value: "eu2", label: "EU 2 (eu-2.ast.checkmarx.net)" },
            { value: "deu", label: "Germany (deu.ast.checkmarx.net)" },
            { value: "ind", label: "India (ind.ast.checkmarx.net)" },
            { value: "anz", label: "ANZ (anz.ast.checkmarx.net)" },
            { value: "sng", label: "Singapore (sng.ast.checkmarx.net)" },
            { value: "mea", label: "UAE (mea.ast.checkmarx.net)" },
          ],
        },
      ],
    },
    {
      title: "Enter your API key",
      items: [
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ region }) => {
      const effectiveRegion = region || "us";
      const astHost = regionToAstHost[effectiveRegion] ?? regionToAstHost.us;
      return `https://${astHost}/api/projects`;
    },
  },
  publicEnvVars: ({ tenant, region }) => ({
    CHECKMARX_TENANT: tenant || "",
    CHECKMARX_REGION: region || "",
  }),
});
